import React from "react";
import { Avatar, Box, Button, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AlertIcon from "assets/icons/Alert.svg";
import { useFormikContext } from "formik";
import { useNavigate } from "react-router-dom";

const SegmentedProgress = ({ value, totalSteps }) => {
  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: "8px",
        gap: { md: 3, xs: 1 },
      }}
    >
      {Array.from({ length: totalSteps }, (_, index) => {
        const isCompleted = index < value;
        return (
          <Box
            key={index}
            sx={{
              flex: 1,
              height: "100%",
              backgroundColor: isCompleted ? "#3CAB5C" : "#ECECEC",
              borderRadius: "10px",
            }}
          />
        );
      })}
    </Box>
  );
};

const StepperForm = ({
  activeStep,
  setActiveStep,
  children,
  stepsNames,
  footerText = true,
  errors,
  touched,
  stepFieldsMap,
  submitStep,
  handleSubmit,
  showCompletionScreen = false,
  position = "fixed",
  formData,
  hasOpenForm = false,
}) => {
  const { validateForm, setTouched, submitForm, isSubmitting } =
    useFormikContext();
  const navigate = useNavigate();
  // Re-entrancy guard for the Next button to avoid rapid double clicks
  const nextLockRef = React.useRef(false);
  const handleNext = async () => {
    console.log(`[StepperForm] handleNext triggered for step ${activeStep} (${stepsNames[activeStep]})`);

    // Block navigation if a license/insurance form is open
    if (hasOpenForm) {
      console.log("[StepperForm] handleNext blocked: license or insurance form is open");
      return;
    }

    // If we're showing the completion screen or currently submitting, don't do anything
    if (showCompletionScreen || activeStep > submitStep || isSubmitting) {
      console.log(`[StepperForm] handleNext blocked: showCompletionScreen=${showCompletionScreen}, activeStep=${activeStep}, submitStep=${submitStep}, isSubmitting=${isSubmitting}`);
      return;
    }

    // Prevent rapid double clicks while validation/submission is in progress
    if (nextLockRef.current) {
      console.log("[StepperForm] handleNext blocked by nextLockRef");
      return;
    }
    nextLockRef.current = true;

    // Get the fields for the current step
    const stepFields = getStepFields(activeStep);
    console.log("[StepperForm] Fields for current step:", stepFields);

    // Mark only the current step's fields as touched to trigger error display
    const touchedFields = {};
    stepFields.forEach((field) => {
      touchedFields[field] = true;
    });
    setTouched(touchedFields);

    // Trigger validation for the current step
    const validationErrors = await validateForm();
    console.log("[StepperForm] Validation results for step:", validationErrors);

    // Check if there are any validation errors for the current step
    const currentStepErrors = {};
    stepFields.forEach(field => {
      if (validationErrors[field]) {
        currentStepErrors[field] = validationErrors[field];
      }
    });

    const hasCurrentStepErrors = Object.keys(currentStepErrors).length > 0;
    
    // Safety check for Stripe step (index 4 in the 7-step flow)
    const isStripeStep = activeStep === 4 || stepFields.includes("stripe_onboarding_complete");
    const stripeComplete = formData?.stripe_onboarding_complete || formData?.stripe_ready || formData?.stripe_charges_enabled;

    if (hasCurrentStepErrors && !(isStripeStep && stripeComplete)) {
      console.log("[StepperForm] Cannot proceed due to errors in current step:", currentStepErrors);
    } else {
      console.log("[StepperForm] No validation errors found for current step (or Stripe complete). Proceeding...");
    }

    try {
      if (!hasCurrentStepErrors || (isStripeStep && stripeComplete)) {
        if (activeStep === submitStep) {
          console.log("[StepperForm] Final step reached, calling submitForm()");
          await submitForm();
          // Don't move to completion screen here - let onSubmit handle it after API success
        } else if (activeStep === stepsNames.length - 1) {
          console.log("[StepperForm] End of steps reached, handling redirection");
          // On the last step, trigger form submission or fallback navigation
          if (handleSubmit) {
            handleSubmit();
          } else {
            navigate("/vendors");
          }
        } else {
          console.log(`[StepperForm] Advancing to step ${activeStep + 1}`);
          // Move to next step
          setActiveStep((prevActiveStep) => prevActiveStep + 1);
        }
      }
    } catch (err) {
      console.error("[StepperForm] Error during handleNext execution:", err);
    } finally {
      nextLockRef.current = false;
    }
  };

  const handleBack = () => {
    // Block navigation if a license/insurance form is open
    if (hasOpenForm) {
      console.log("[StepperForm] handleBack blocked: license or insurance form is open");
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Check if there are any validation errors for the current step
  const hasStepErrors = () => {
    if (!errors || !touched) return false;

    // Get the fields for the current step based on the step index
    const stepFields = getStepFields(activeStep);

    // Check if any of the current step's fields have errors and are touched
    const errorsInCurrentStep = stepFields.filter((field) => errors[field] && touched[field]);
    if (errorsInCurrentStep.length > 0) {
      console.log(`[StepperForm] nextDisabled check: Found ${errorsInCurrentStep.length} errors in current step:`, errorsInCurrentStep.map(f => `${f}: ${errors[f]}`));
    }
    return errorsInCurrentStep.length > 0;
  };

  // Helper function to get fields for each step
  const getStepFields = (stepIndex) => {
    return stepFieldsMap[stepIndex] || [];
  };

  // Determine if current step is Stripe and Stripe is complete
  const isStripeCurrentStep = (stepFieldsMap?.[activeStep] || []).includes(
    "stripe_onboarding_complete"
  ) || activeStep === 4;
  const stripeOnboardingComplete = !!(
    formData?.stripe_onboarding_complete || formData?.stripe_ready || formData?.stripe_charges_enabled
  );
  const nextDisabled = (hasStepErrors() && !(isStripeCurrentStep && stripeOnboardingComplete)) || isSubmitting || hasOpenForm;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: { md: 3, xs: 2 },
        paddingY: { md: "20px", xs: "20px" },
      }}
    >
      {!showCompletionScreen && activeStep <= submitStep && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            maxWidth: "1440px",
            width: "100%",
            paddingX: { md: 3, xs: 2 },
            margin: "auto",
          }}
        >
          <Typography
            sx={{ color: "#242424", mb: 0, lineHeight: "26px" }}
            variant="h2"
          >
            Step {activeStep + 1}
            <span className="text-[#767676]">/{stepsNames.length}</span> -
            {stepsNames[activeStep]}
          </Typography>
          <Box sx={{ width: "100%", display: "flex" }}>
            <SegmentedProgress
              value={activeStep + 1}
              totalSteps={stepsNames.length}
            />
          </Box>
        </Box>
      )}
      <div className={`${position === "fixed" ? "pb-[90px]" : ""}`}>
        <Box
          sx={{
            maxWidth: "1440px",
            width: "100%",
            paddingX: { md: 3, xs: 2 },
            margin: "auto",
            minHeight: {
              md: `${position === "fixed" ? "560px" : "auto"}`,
              xs: `${position === "fixed" ? "400px" : "auto"}`,
            },
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            boxSizing: "border-box",
          }}
        >
          {children}
        </Box>
      </div>

      {footerText && !showCompletionScreen && activeStep <= submitStep && (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            maxWidth: "1440px",
            width: "100%",
            paddingX: { md: 3, xs: 2 },
            margin: "auto",
          }}
        >
          <Avatar src={AlertIcon} sx={{ width: 18, height: 18 }} />
          <Typography variant="h4" sx={{ color: "#242424" }}>
            If this is an emergency, please call 9-1-1. Properteasy does not
            dispatch emergency services.
          </Typography>
        </Box>
      )}
      {!showCompletionScreen && activeStep <= submitStep && (
        <Box
          sx={{
            position: position,
            bottom: 0,
            width: "100%",
            backgroundColor: "#fff",
            zIndex: 1000,
          }}
        >
          {hasOpenForm && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                maxWidth: "1440px",
                width: "100%",
                paddingX: { md: 3, xs: 2 },
                paddingTop: 2,
                margin: "auto",
              }}
            >
              <Typography
                sx={{
                  color: "#d97706",
                  fontSize: "14px",
                  fontWeight: 500,
                  textAlign: "center",
                }}
              >
                Please save or cancel the open form before continuing
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              maxWidth: "1440px",
              width: "100%",
              paddingX: { md: 3, xs: 2 },
              paddingBottom: { md: 3, xs: 2 },
              paddingTop: 0,
              margin: "auto",
            }}
          >
            <Button
              sx={{
                padding: "12px 16px",
                fontSize: "16px",
                maxHeight: "48px",
              }}
              disabled={activeStep === 0 || isSubmitting || hasOpenForm}
              onClick={handleBack}
              variant="outlined"
              startIcon={<ArrowBackIcon />}
            >
              Previous
            </Button>
            <Button
              sx={{
                backgroundColor: "#3CAB5C",
                padding: "12px 16px",
                fontSize: "16px",
                maxHeight: "48px",
                "&:hover": {
                  backgroundColor: "#3CAB5C",
                },
              }}
              onClick={handleNext}
              variant="contained"
              color="primary"
              endIcon={<ArrowForwardIcon />}
              disabled={nextDisabled}
            >
              {isSubmitting
                ? "Submitting..."
                : activeStep === stepsNames.length - 1
                ? submitStep !== 8
                  ? submitStep === 3
                    ? "Finish"
                    : "Go to Dashboard"
                  : "Next"
                : "Next"}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default StepperForm;
